import { useEffect, useState } from 'react';
import { Input } from '../../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  canEmitSpatialSubOperator,
} from '../../utils/spatialFilterSql';
import { getSpatialSubOperatorLabel } from '../../utils/filterUtils';
import type {
  FilterCondition,
  SpatialFilterContext,
  SpatialSubOperator,
} from '../../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

export type SpatialFilterFieldsProps = {
  condition: FilterCondition;
  engine: DatabaseEngineId;
  spatialContext: SpatialFilterContext;
  onChange: (updates: Partial<FilterCondition>) => void;
};

const SUB_OPS: SpatialSubOperator[] = ['intersects_bbox', 'within_distance', 'intersects_polygon'];

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(4);
}

/**
 * Field cluster for the `spatial` filter operator: a sub-operator select plus a
 * read-only bbox / point display and a distance input. The bbox and point are
 * populated from the map (the Table Explorer "Filter by map view" button and
 * map-click) — they are never typed here. Sub-operators the engine / CRS can't
 * emit (via `canEmitSpatialSubOperator`) are disabled with an explanatory
 * tooltip (repo rule: disabled controls explain WHY).
 */
export function SpatialFilterFields({ condition, engine, spatialContext, onChange }: SpatialFilterFieldsProps) {
  const subOp: SpatialSubOperator = condition.spatialSubOperator ?? 'intersects_bbox';
  const [distanceDraft, setDistanceDraft] = useState<string>(
    condition.distanceMeters != null ? String(condition.distanceMeters) : '',
  );

  useEffect(() => {
    setDistanceDraft(condition.distanceMeters != null ? String(condition.distanceMeters) : '');
  }, [condition.distanceMeters]);

  const commitDistance = () => {
    const parsed = Number(distanceDraft);
    if (distanceDraft.trim() !== '' && Number.isFinite(parsed) && parsed > 0) {
      if (parsed !== condition.distanceMeters) onChange({ distanceMeters: parsed });
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-1 max-w-md" data-testid="spatial-filter-fields">
      <Select
        value={subOp}
        onValueChange={(v: SpatialSubOperator) => onChange({ spatialSubOperator: v })}
      >
        <SelectTrigger className="h-6 w-auto min-w-[120px] px-2 text-xs" data-testid="spatial-suboperator-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent portalled={false}>
          {SUB_OPS.map((op) => {
            const enabled = canEmitSpatialSubOperator(engine, op, spatialContext);
            return (
              <SelectItem
                key={op}
                value={op}
                disabled={!enabled}
                className="text-xs"
                title={
                  enabled
                    ? undefined
                    : op === 'within_distance'
                      ? 'Distance filters need a geographic (SRID 4326) column'
                      : op === 'intersects_polygon'
                        ? 'Draw-area filters need a geometry/geography column on a supported engine'
                        : 'Not supported for this column / engine'
                }
              >
                {getSpatialSubOperatorLabel(op)}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {subOp === 'intersects_bbox' && (
        <span className="text-xs text-muted-foreground px-1" data-testid="spatial-bbox-display">
          {condition.bbox ? (
            <>
              [{fmt(condition.bbox[0])}, {fmt(condition.bbox[1])}] – [{fmt(condition.bbox[2])}, {fmt(condition.bbox[3])}]
            </>
          ) : (
            <span className="italic">use “Filter by map view”</span>
          )}
        </span>
      )}

      {subOp === 'intersects_polygon' && (
        <span className="text-xs text-muted-foreground px-1" data-testid="spatial-polygon-display">
          {condition.polygon && condition.polygon.length >= 3 ? (
            <>{condition.polygon.length}-point area</>
          ) : (
            <span className="italic">draw an area on the map</span>
          )}
        </span>
      )}

      {subOp === 'within_distance' && (
        <>
          <Input
            value={distanceDraft}
            onChange={(e) => setDistanceDraft(e.target.value)}
            onBlur={commitDistance}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitDistance();
              }
            }}
            type="number"
            min={0}
            placeholder="metres"
            className="h-6 w-24 text-xs"
            aria-label="Distance in metres"
            data-testid="spatial-distance-input"
          />
          <span className="text-xs text-muted-foreground">m of</span>
          <span className="text-xs text-muted-foreground px-1" data-testid="spatial-point-display">
            {condition.point ? (
              <>
                [{fmt(condition.point[0])}, {fmt(condition.point[1])}]
              </>
            ) : (
              <span className="italic">click the map to set centre</span>
            )}
          </span>
        </>
      )}
    </span>
  );
}
